import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { thumb } from "../lib/api";
import { Instagram, ArrowRight, ArrowUpRight } from "lucide-react";

export default function About() {
  const [settings, setSettings] = useState({});
  const [creators, setCreators] = useState([]);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data || {})).catch(() => {});
    api.get("/creators").then(({ data }) => setCreators(data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page" className="pt-32 pb-24 fade-in">
      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-4">About Cake Society</p>
        <h1 className="font-serif font-black text-5xl sm:text-6xl text-[#121212] max-w-4xl leading-[1.05]">
          Cake is the culture. Come create with us.
        </h1>
      </div>

      {/* 1. Our Story */}
      <section data-testid="about-our-story" className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-16 mt-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-5">Our Story</p>
        <div className="space-y-6 text-lg leading-relaxed text-stone-700">
          <p>
            Cake Society is a creative cake studio founded by <strong className="text-[#121212]">Kayla Giddings</strong>,
            a cake artist, educator, 4x Food Network Champion, and competitor on Netflix's <em>Is It Cake?</em>
          </p>
          <p>
            After years of teaching sold-out cake camps, competing on national television, and creating cakes for
            people who love the unexpected, Kayla wanted to build a space where creativity feels exciting, welcoming,
            and unforgettable.
          </p>
          <p>
            Cake Society brings together hands-on classes, kids camps, Open Studio, online tutorials, memberships,
            and Guest Creators — all designed to help people learn, create, celebrate, and belong.
          </p>
        </div>
        <p className="mt-10 font-serif font-black text-3xl sm:text-4xl text-[#121212] leading-tight">
          Cake is not just dessert. <span className="lime-swipe italic">Cake is the culture.</span>
        </p>
      </section>

      {/* 2. Meet the Founder */}
      <section data-testid="about-founder" className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-16 mt-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="aspect-[4/5] overflow-hidden border-2 border-[#121212] bg-[#A89D8A]/20 order-1 lg:order-none">
          {settings.founder_image && (
            <img src={thumb(settings.founder_image, 800)} alt={settings.founder_name || "Founder"} className="w-full h-full object-cover" />
          )}
        </div>
        <div>
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-4">Meet the Founder</p>
          <h2 className="font-serif font-black text-4xl sm:text-5xl text-[#121212] leading-[1.05]">{settings.founder_name || "Kayla Giddings"}</h2>
          {settings.founder_bio && <p className="mt-6 text-lg leading-relaxed text-stone-700">{settings.founder_bio}</p>}
        </div>
      </section>

      {/* 3. Meet the Creators */}
      <section data-testid="about-creators" className="bg-[#121212] text-[#F2E9D8] mt-24 py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33] mb-4">Meet the Creators</p>
          <h2 className="font-serif font-black text-4xl sm:text-5xl leading-[1.05] max-w-2xl">
            The makers behind the magic.
          </h2>
          <p className="mt-5 text-[#F2E9D8]/70 text-lg max-w-2xl">
            Guest creators and resident teachers who bring their own craft, style, and energy to the studio.
          </p>

          {creators.length === 0 ? (
            <p className="mt-12 text-[#F2E9D8]/50">New creators are joining soon — check back shortly.</p>
          ) : (
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {creators.map((c) => (
                <div key={c.id} data-testid={`about-creator-${c.id}`} className="bg-[#F2E9D8] text-[#121212] border-2 border-[#F2E9D8] flex flex-col">
                  <div className="aspect-square overflow-hidden border-b-2 border-[#121212] bg-[#A89D8A]/20">
                    {c.photo_url
                      ? <img src={thumb(c.photo_url, 600)} alt={c.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center font-serif font-black text-6xl text-[#121212]/40">{c.name?.[0]}</div>}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-serif font-black text-2xl leading-tight">{c.name}</h3>
                    {c.specialties?.length > 0 && (
                      <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#121212]/70 mt-2">{c.specialties.slice(0, 3).join(" · ")}</p>
                    )}
                    {c.bio && <p className="mt-3 text-sm text-[#121212]/80 line-clamp-3">{c.bio}</p>}
                    <div className="mt-auto pt-5 flex items-center justify-between gap-3">
                      {c.instagram
                        ? <a href={`https://instagram.com/${c.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" data-testid={`about-creator-ig-${c.id}`} className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-bold hover:text-[#121212]/60"><Instagram size={15} /> {c.instagram.startsWith("@") ? c.instagram : `@${c.instagram}`}</a>
                        : <span />}
                      <Link to={`/creators/${c.id}`} data-testid={`about-creator-classes-${c.id}`} className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-bold hover:gap-2.5 transition-all">Classes <ArrowRight size={13} /></Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Small callout */}
          <div data-testid="about-creators-callout" className="mt-12 border border-[#F2E9D8]/20 p-7 sm:p-9 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div>
              <p className="font-serif font-black text-2xl">Want to teach at Cake Society?</p>
              <p className="mt-1 text-[#F2E9D8]/70">Bring your creative workshop to our studio as a Guest Creator.</p>
            </div>
            <Link to="/guest-creators" data-testid="about-apply-creator-callout" className="shrink-0 px-6 py-3 bg-[#C6FF33] text-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#b3ec1f] transition-colors">
              Apply to Teach
            </Link>
          </div>
        </div>
      </section>

      {/* 4. The Studio */}
      <section data-testid="about-studio" className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-16 mt-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-4">The Studio</p>
          <h2 className="font-serif font-black text-4xl sm:text-5xl text-[#121212] leading-[1.05]">A space made for making.</h2>
          <p className="mt-6 text-lg leading-relaxed text-stone-700">
            Our studio is open throughout the week for hands-on classes, kids camps, drop-in Open Studio sessions,
            online tutorial production, and Guest Creator workshops. Every corner is built to make creativity feel exciting,
            welcoming, and unforgettable — whether you come for a single class or stay to teach your own.
          </p>
        </div>
        <div className="aspect-[4/3] overflow-hidden border-2 border-[#121212] bg-[#A89D8A]/20">
          {settings.studio_image && (
            <img src={thumb(settings.studio_image, 800)} alt="The Cake Society studio" className="w-full h-full object-cover" />
          )}
        </div>
      </section>

      {/* 5. Want to Teach */}
      <section data-testid="about-teach" className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-16 mt-24">
        <div className="bg-[#C6FF33] text-[#121212] border-2 border-[#121212] p-10 sm:p-14 text-center">
          <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4">Guest Creators</p>
          <h2 className="font-serif font-black text-4xl sm:text-5xl leading-[1.05] max-w-2xl mx-auto">Want to teach at Cake Society?</h2>
          <p className="mt-5 text-lg max-w-xl mx-auto text-[#121212]/80">
            Bring your creative workshop to our studio. We host guest creators throughout the month — tell us what you'd love to teach.
          </p>
          <Link to="/guest-creators" data-testid="about-apply-creator-btn" className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[#121212] text-[#F2E9D8] text-xs tracking-[0.25em] uppercase font-bold hover:bg-black transition-colors">
            Apply to Teach <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
